import { createPersistentItem, type PersistentItem } from '@amazon/vinyl-util'
import { isOneOf, number, object, string, type ObjectSchema } from '@amazon/vinyl-validation'
import { type AuthTokenWithRefresh } from '@amzn/vinyl-amzn'

// If the API key changes or there is another reason to wipe auth persistence, increment this version.
export const AUTH_VERSION = 1

type AuthTokenWithRefreshVersioned = AuthTokenWithRefresh & {
    readonly version: typeof AUTH_VERSION
}

export const authTokenValidator: ObjectSchema<AuthTokenWithRefreshVersioned> = object({
    refreshToken: string(),
    expiresAt: number(),
    accessToken: string(),
    version: isOneOf(AUTH_VERSION),
})

// Backing store validates on the versioned shape: a token missing `version` (the
// pre-versioning shape) or carrying a stale version fails the validator, so
// get() falls back to null — the wipe. Bumping AUTH_VERSION invalidates all
// previously-persisted tokens.
const versionedPersistence = createPersistentItem<AuthTokenWithRefreshVersioned | null>({
    key: 'auth',
    defaultValue: null,
    validator: authTokenValidator.orNull().assert,
})

// Facade stamping `version` on every write, so all writers — LwaAuth AND
// vinyl-amzn's cblSignIn (which persists a plain AuthTokenWithRefresh) — store a
// token the validator accepts. Callers see the base AuthTokenWithRefresh shape.
export const localSignInPersistence: PersistentItem<AuthTokenWithRefresh | null> = {
    get: () => versionedPersistence.get(),
    set: (value) =>
        versionedPersistence.set(value == null ? null : { ...value, version: AUTH_VERSION }),
    remove: () => versionedPersistence.remove(),
}
